import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { IAddEditTableForm } from "../../../../../../helpers/appHelpers";
import ModalComponent from "../../../../../../components/modals/ModalFormComponent";
import Label from "../../../../../../components/form_fields/Label";
import OrangeButton from "../../../../../../components/buttons/OrangeButton";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import { useManageTableWithSearchParamsData } from "../../../../../../helpers/useManageTable";
import {
	useCreateNationality,
	useUpdateNationality,
} from "../../../../../../services/nationality.service";

interface IFormInputs {
	name: string;
	countryCode: string;
	flag: string;
}

type INationalitiesModalForm = IAddEditTableForm & {
	reloadTableData?: () => void;
};

const NationalitiesModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	reloadTableData,
}: INationalitiesModalForm) => {
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = React.useState(false);
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : "",
			countryCode: isEditForm ? formDataValues?.countryCode : "",
			flag: isEditForm ? formDataValues?.symbol : "",
		},
	});

	const { mutate: createNationality } = useCreateNationality();
	const { mutate: updateNationality } = useUpdateNationality();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		data.flag = data.countryCode || undefined;

		if (isEditForm) {
			updateNationality(
				{ payload: data, id: formDataValues?.id },
				{
					onSuccess: (updatedData: any) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createNationality(
				{ payload: data },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		reset();
		setOpen(false);
	};

	return (
		<ModalComponent
			width={700}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form className="mt-4" onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							defaultValue={isEditForm ? formDataValues?.name : ""}
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Country Code" for="countryCode" />
						<input
							defaultValue={isEditForm ? formDataValues?.countryCode : ""}
							placeholder={"Enter country code"}
							type="text"
							{...register("countryCode", {
								required: "Enter country code here",
							})}
							id="countryCode"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.countryCode
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.countryCode && (
							<p className="text-red-500 text-sm mt-1">
								{errors.countryCode.message}
							</p>
						)}
					</div>
				</div>

				<div className="w-full flex justify-end mt-10">
					<OrangeButton
						title={`${
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Nationality"
									: "Add New Nationality"
						}`}
						className={`px-8`}
						type={"submit"}
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default NationalitiesModalForm;
